import { LoginStep, UserData } from "./types";

import { FC, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { AUTH_API } from "../../../api/auth";
import AuthBlockContainer from "../../../components/AuthBlockContainer";
import { LOCAL_LOGIN } from "../../../constants.ts";
import { useAppDispatch } from "../../../hooks/redux-hooks.ts";
import { useSSE } from "../../../layout/SSELayout";
import {
  setIsNewUser,
  setProfile,
} from "../../../store/slices/profileSlice.ts";
import {
  COOKIE_COMPANY_ID,
  COOKIE_REFRESH_TOKEN_NAME,
  COOKIE_TOKEN_NAME,
  COOKIE_USER_ID,
  setCookie,
} from "../../../utils/cookie.ts";
import EmailUserInfo from "../EmailUserInfo";
import Information from "../Information/index.tsx";
import EmailVerification from "./EmailVerification";
import Initial from "./Initial";
import Password from "./Password";
import { LOGIN_TITLE_MAP, removeQueryParams } from "./helpers.ts";

import { SignInResponseType } from "../../../api/auth/types.ts";
import { ApiResponse } from "../../../api/types.ts";
import { AuthStepComponentProps } from "../types.ts";

const stepComponents: Record<
  LoginStep,
  FC<AuthStepComponentProps<LoginStep>>
> = {
  initial: Initial,
  password: Password,
  verification: EmailVerification,
  emailUserInfo: EmailUserInfo,
};

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { setToken } = useSSE();

  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState<LoginStep>("initial");
  const [userData, setUserData] = useState<UserData | null>(null);
  const [email, setEmail] = useState(LOCAL_LOGIN || "");
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  useEffect(() => {
    const code = searchParams.get("code");
    const scope = searchParams.get("scope");
    const authuser = searchParams.get("authuser");
    const prompt = searchParams.get("prompt");
    const refCode = searchParams.get("state");
    const type = searchParams.get("type");

    const handleAuth = async (
      authFunction: () => Promise<ApiResponse<SignInResponseType>>,
    ) => {
      setIsAuthorizing(true);
      const { data, error } = await authFunction();
      if (data) {
        setTimeout(() => {
          if (data?.data?.user?.isNewUser) {
            setUserData({
              isNewUser: data?.data?.user?.isNewUser || false,
              user: data.data.user,
              token: data.data.tokenData.token,
              refreshToken: data.data.tokenData.refreshToken,
              companyId: data.data.user.companies[0]?.companyId,
              userId: data.data.user.userId,
            });
            setIsAuthorizing(false);
            removeQueryParams();
            setStep("emailUserInfo");
          } else {
            dispatch(setIsNewUser(data?.data?.user?.isNewUser || false));
            dispatch(setProfile(data.data.user));
            setCookie(COOKIE_TOKEN_NAME, data.data.tokenData.token);
            setToken(data.data.tokenData.token);
            setCookie(
              COOKIE_REFRESH_TOKEN_NAME,
              data.data.tokenData.refreshToken,
            );
            setCookie(
              COOKIE_COMPANY_ID,
              data.data.user.companies[0]?.companyId,
            );
            setCookie(COOKIE_USER_ID, data.data.user.userId);
            navigate("/overview", { replace: true });
          }
        }, 0);
      } else {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        removeQueryParams();
        setIsAuthorizing(false);
      }
    };

    if (refCode && code && scope && authuser && prompt) {
      setSearchParams({
        code,
        scope,
        authuser,
        prompt,
        refCode,
      });
    }

    if (code && scope && authuser && prompt) {
      void handleAuth(AUTH_API.signInWithGoogle);
    } else if (type === "apple" && code) {
      void handleAuth(AUTH_API.signInWithApple);
    } else if (code) {
      void handleAuth(AUTH_API.signInWithGithub);
    }
    
  }, [searchParams]);

  const CurrentStepComponent = stepComponents[step];
  return (
    <>
      {isAuthorizing ? (
        <Information text="Authorizing..." />
      ) : (
        <AuthBlockContainer Icon={null} title={LOGIN_TITLE_MAP[step]}>
          <CurrentStepComponent
            {...{ setEmail, setStep, email, isAuthorizing, userData }}
          />
        </AuthBlockContainer>
      )}
    </>
  );
};

export default Login;
